<template>
  <div class="client-details">
    <portal to="sidebar-menu">

      <div>
        <nav-back-button to="/contacts/clients">
          {{ $t('client.management.menu.back') }}
        </nav-back-button>
        <client-card :client="client"/>
        <div class="row">
          <div class="col-12">
            <nav-list :items="navigationItems"
                      @on-delete-click="deleteClient"
                      @on-approve-click="approveClient"
                      @on-reject-click="rejectClient"
            >
            </nav-list>
            <base-button class="mt-2" @click="onMergeClick" block outline>
              {{ $t('clients.details.merge') }}
            </base-button>
          </div>
        </div>
      </div>

    </portal>
    <modal :show.sync="showMergeModal" :title="$t('clients.details.merge')">
      <fg-input :label="$t('clients.details.mergeSelect')"
                required>
        <client-autocomplete v-model="mergeModel.selectedClient" :exclude-organizations="[client]"/>
      </fg-input>
      <fg-input required
                :label="$t('clients.details.mergeSelect.agree')"
                class="mt-4">
        <base-checkbox v-model="mergeModel.acceptMerge">
          <template v-slot:label>
            <div class="mt-1">
              <strong>{{ mergeModel.selectedClient.name || '- -' }}</strong>
              {{ $t('clients.details.merge.tip') }}
              <strong>{{ client.name }}. </strong>
              <strong>{{ mergeModel.selectedClient.name || '- -' }}</strong>
              {{ $t('clients.details.merge.tip2') }}
            </div>
          </template>
        </base-checkbox>
      </fg-input>

      <div class="flex w-full justify-center">
        <submit-button :disabled="!mergeModel.selectedClient.uuid || !mergeModel.acceptMerge"
                       :loading="loadingMerge"
                       @click="mergeClient">
        </submit-button>
      </div>
    </modal>
    <router-view></router-view>
  </div>
</template>
<script>
import clientModule from 'src/store/modules/client'
import { entities } from 'src/config/routeTypes'
import { mapMutations, mapState } from 'vuex'
import NavList from 'src/components/List/NavList'
import ClientService from 'src/services/ClientService'
import ClientCard from 'src/components/Cards/ClientCard'
import ClientAutocomplete from '@/components/Autocomplete/ClientAutocomplete'

export default {
  name: 'client-management',
  components: {
    NavList,
    ClientCard,
    ClientAutocomplete,
  },
  data () {
    return {
      error: null,
      deleteActive: false,
      loadingMerge: false,
      showMergeModal: false,
      mergeModel: {
        selectedClient: {},
        acceptMerge: false,
      },
    }
  },
  computed: {
    ...mapState({
      client: state => state.client.currentClient
    }),
    shouldCheckClient () {
      return this.client && this.client.status === 'check'
    },
    navigationItems () {
      return [
        {
          name: this.$t('generic.general'),
          type: 'group'
        },
        {
          routeName: 'client_dashboard',
          name: this.$t('client.management.menu.dashboard')
        },
        {
          routeName: 'client_edit',
          name: this.$t('client.management.menu.edit'),
          hide: !this.$can('admin-clients-crud')
        },
        {
          routeName: 'client_financial',
          name: this.$t('client.management.menu.financial'),
          hide: !this.$can('admin-clients-crud')
        },
        {
          routeName: 'client_users',
          name: this.$t('client.management.menu.users')
        },
        {
          routeName: 'client_jobs',
          name: this.$t('client.management.menu.jobs')
        },
        {
          name: this.$t('generic.ecommerce'),
          type: 'group'
        },
        {
          routeName: 'client_orders',
          name: this.$t('client.management.menu.orders'),
        },
        {
          routeName: 'client_credits',
          name: this.$t('client.management.menu.credit')
        },
        {
          routeName: 'client_contracts',
          name: this.$t('client.management.menu.contracts')
        },
        {
          routeName: 'client_products',
          name: this.$t('client.management.menu.products')
        },
        {
          routeName: 'client_subscriptions',
          name: this.$t('client.management.menu.subscriptions')
        },
        {
          routeName: 'client_webshop',
          name: this.$t('client.management.menu.webshop'),
        },
        {
          name: this.$t('generic.settings'),
          type: 'group'
        },
        {
          routeName: 'client_tokens',
          name: this.$t('client.management.menu.tokens'),
        },
        {
          routeName: 'client_third_parties',
          name: this.$t('client.management.menu.thirdParties'),
        },
        {
          routeName: 'client_logs',
          name: this.$t('client.management.menu.logs'),
          hide: !this.$can('admin-wip'),
        },
        {
          name: this.$t('generic.approve'),
          hide: !this.$can('admin-jobs-crud') || !this.shouldCheckClient,
          title: 'approve'
        },
        {
          name: this.$t('generic.reject'),
          hide: !this.$can('admin-jobs-crud') || !this.shouldCheckClient,
          title: 'reject'
        },
        {
          name: this.$t('generic.delete.title'),
          hide: !this.$can('admin-jobs-crud'),
          title: 'delete'
        },
      ]
    }
  },
  methods: {
    ...mapMutations('client', [clientModule.types.SET_CURRENT_CLIENT]),
    async deleteClient () {
      await this.$confirm(this.$t('generic.delete.confirm', {title: this.client.name, entity: entities.CLIENT}), {
        dangerouslyUseHTMLString: true
      })
      await ClientService.delete(this.client.uuid)
      this.$router.push('/contacts/clients')
    },
    async approveClient () {
      let newStatus = 'active'
      await ClientService.verify(this.client.uuid, {
        status: newStatus
      })
      this.client.status = newStatus
    },
    async rejectClient () {
      let newStatus = 'blocked'
      await ClientService.verify(this.client.uuid, {
        status: newStatus
      })
      this.client.status = newStatus
    },
    onMergeClick () {
      this.showMergeModal = true
    },
    async mergeClient () {
      try {
        this.loadingMerge = true
        await ClientService.mergeClient(this.$route.params.id, {
          client_uuid: this.mergeModel.selectedClient.uuid
        })
        this.showMergeModal = false
        await this.getClient()
      } finally {
        this.loadingMerge = false
      }
    },
    async getClient() {
      let res = await ClientService.getById(this.$route.params.id, {
        with: 'invoices'
      })
      this[clientModule.types.SET_CURRENT_CLIENT](res.data)
    }
  },
  async beforeRouteEnter (to, from, next) {
    try {
      let id = to.params.id
      let res = await ClientService.getById(id, {
        with: 'invoices'
      })
      let client = res.data
      if (client) {
        next(vm => {
          vm[clientModule.types.SET_CURRENT_CLIENT](client)
        })
      } else {
        next(vm => vm.error = 'No data')
      }
    } catch (e) {
      next(vm => vm.error = e)
    }
  },
}
</script>
<style lang="scss">
.client-details {
  .client-avatar {
    width: 150px;
    height: 100%;
  }

  .layout-margin {
    margin-top: 62px;
  }
}
</style>
